// The Hydraulic Clasp: where a dragged piece goes, and whether it may (§26 C11).
// Its own file because `moveGen` and `campaign/cards.ts` both import it.

import {
  castleRightsMask,
  COORD_MAX,
  COORD_MIN,
  fileOf,
  pieceColor,
  rankOf,
  square
} from "../board";
import { isBash, type Move, victimOf } from "../move";
import {
  armourLeft,
  claspedTo,
  expireClasp,
  hasCell,
  moatLeft,
  pieceAt,
  pieceIdAt,
  type Position,
  setClasp
} from "../position";
import { blowStep, shovePiece } from "./blow";

/**
 * Where a piece on `partner` ends up when the mover goes `from` -> `to`, or -1.
 *
 * Built from coordinates, not by adding `to - from`: a packed offset wraps at
 * the file band. Off the coordinate range answers -1 and the move gets no drag.
 */
export function dragSquare(from: number, to: number, partner: number): number {
  const file = fileOf(partner) + (fileOf(to) - fileOf(from));
  const rank = rankOf(partner) + (rankOf(to) - rankOf(from));
  if (file < COORD_MIN || file > COORD_MAX) return -1;
  if (rank < COORD_MIN || rank > COORD_MAX) return -1;
  return square(file, rank);
}

/**
 * May the dragged piece stand on `at`?
 *
 * An empty cell, counting the square the mover is leaving as empty. Refused:
 * the mover's destination and where a bash drove its target. An en passant
 * victim's square reads as occupied here.
 */
function standable(pos: Position, move: Move, at: number): boolean {
  const from = move.from;
  const to = move.to;
  if (at === to) return false;
  if (!hasCell(pos, at)) return false;
  // A wall is not somewhere a drag may put a piece either (§26 C13).
  if (moatLeft(pos, at) > 0) return false;
  // Nor onto any building (§31 S1): a drag is no plow, and breaks nothing.
  if (pos.structures.size > 0 && pos.structures.has(at)) return false;
  if (at !== from && pieceAt(pos, at) !== 0) return false;
  // The shove landed exactly when the attacker took the target's square.
  if (isBash(move) && to === move.shoved && at === move.shoved + blowStep(from, move.shoved)) {
    return false;
  }
  return true;
}

/**
 * The square this move drags a clamped partner off, or -1.
 *
 * Four cases answer -1, none of which makes the move illegal: nothing is
 * clamped to the mover; the partner is no longer on its square; the mover does
 * not move (a knight that bashed and bounced); or the move takes, takes in
 * passing, or bashes the partner.
 *
 * A locked piece is not dragged. That is the fourth door onto the Stasis
 * Field's "cannot move", and it is checked here as well as at the card because
 * the clamp can be played before the lock is.
 */
export function dragOf(pos: Position, move: Move): number {
  if (!pos.clasp) return -1;
  const from = move.from;
  const partner = claspedTo(pos, from);
  if (partner < 0 || from === move.to) return -1;
  if (pieceAt(pos, partner) === 0) return -1;
  if (partner === victimOf(move, pieceColor(pieceAt(pos, from)))) return -1;
  if (armourLeft(pos, partner) > 0) return -1;
  return partner;
}

/**
 * Can this move be played with its drag — and stamp the drag onto it?
 *
 * Called per generated move of a clamped piece, behind a `pos.clasp` test
 * hoisted out of the generation loop, so perft is untouched.
 */
export function claspMove(pos: Position, move: Move): boolean {
  const partner = dragOf(pos, move);
  if (partner < 0) return true;
  const at = dragSquare(move.from, move.to, partner);
  if (at < 0 || !standable(pos, move, at)) return false;
  move.dragged = partner;
  return true;
}

/**
 * Drag the clamped piece, after the mover has left its square.
 *
 * The drag often lands on the square the mover just vacated, so it has to run
 * after the mover has gone. `shovePiece` rather than `placeStep`/`settleStep`,
 * because the industry half belongs in `applyMove`; the partner keeps its
 * facing and does not promote.
 *
 * It does not clear `pos.clasp` — `expireClasp` does that once per move — so
 * the clamp is read-only across make/unmake and needs no `Undo` field.
 */
export function applyDrag(pos: Position, move: Move): void {
  const partner = move.dragged;
  if (partner < 0) return;
  const at = dragSquare(move.from, move.to, partner);
  shovePiece(pos, partner, at);
  // A dragged rook or king loses its castling rights, as a moved one does.
  pos.castling &= castleRightsMask(partner) & castleRightsMask(at);
}

/**
 * Carry the clamp across one action (§31 C8: it lasts the turn).
 *
 * Follows the two bodies by piece id from `before`; dropped when either is
 * gone or they no longer touch (a bash drove one off, a capture took it).
 * Unstamped pieces (id 0) keep the squares only while both stand.
 */
export function followClasp(before: Position, after: Position): void {
  const clasp = after.clasp;
  if (!clasp) return;
  const idA = pieceIdAt(before, clasp.a);
  const idB = pieceIdAt(before, clasp.b);
  let a = -1;
  let b = -1;
  if (idA > 0 && idB > 0) {
    for (const [sq, id] of after.pieceId) {
      if (id === idA) a = sq;
      else if (id === idB) b = sq;
    }
  } else if (pieceAt(after, clasp.a) !== 0 && pieceAt(after, clasp.b) !== 0) {
    a = clasp.a;
    b = clasp.b;
  }
  const touching =
    a >= 0 &&
    b >= 0 &&
    Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b))) === 1;
  if (!touching) expireClasp(after);
  else if (a !== clasp.a || b !== clasp.b) setClasp(after, a, b, clasp.side);
}

/** Put the dragged piece back, before the mover returns to its square. */
export function undoDrag(pos: Position, move: Move): void {
  const partner = move.dragged;
  if (partner < 0) return;
  shovePiece(pos, dragSquare(move.from, move.to, partner), partner);
}
